<script setup>
import { ref, computed, onMounted, watch } from 'vue';
import SlotsConfigAPI from 'dashboard/api/omni/slotsConfig';

const props = defineProps({
  agentBotId: { type: Number, default: null },
  excludeSlotId: { type: [Number, String], default: null },
});

const emit = defineEmits(['pick']);

const fields = ref([]);
const loading = ref(false);
const error = ref('');
const search = ref('');

const filteredFields = computed(() => {
  if (!search.value) return fields.value;
  const q = search.value.toLowerCase();
  return fields.value.filter(f =>
    (f.name || '').toLowerCase().includes(q) ||
    (f.description || '').toLowerCase().includes(q) ||
    (f.source_slot || '').toLowerCase().includes(q)
  );
});

const groupedFields = computed(() => {
  const groups = {};
  filteredFields.value.forEach(f => {
    const key = f.source_slot || '—';
    if (!groups[key]) groups[key] = [];
    groups[key].push(f);
  });
  return groups;
});

const sourceIcon = type => {
  switch (type) {
    case 'restriction':
      return 'i-lucide-lock';
    case 'response_instruction':
      return 'i-lucide-message-square';
    case 'reference':
      return 'i-lucide-link';
    default:
      return 'i-lucide-tag';
  }
};

const loadFields = async () => {
  if (!props.agentBotId) return;
  loading.value = true;
  error.value = '';
  try {
    const { data } = await SlotsConfigAPI.getFieldsCatalog(props.agentBotId, {
      excludeSlotId: props.excludeSlotId,
    });
    fields.value = data?.fields || [];
  } catch (err) {
    error.value = err?.response?.data?.error || err.message;
  } finally {
    loading.value = false;
  }
};

const pickField = field => {
  emit('pick', { token: `{{field.${field.name}}}`, field });
};

onMounted(loadFields);
watch(() => props.agentBotId, loadFields);
</script>

<template>
  <div class="text-xs">
    <div class="flex items-center gap-2 mb-2">
      <input
        v-model="search"
        type="text"
        :placeholder="$t('OMNI.SLOTS.API_AUTOMATIONS.SEARCH_FIELDS', 'Buscar field...')"
        class="flex-1 px-2 py-1 text-xs border border-slate-300 dark:border-slate-600 rounded bg-white dark:bg-slate-700"
      />
      <button
        type="button"
        class="p-1 text-slate-500 hover:text-slate-900 dark:hover:text-slate-100"
        :title="$t('OMNI.SLOTS.API_AUTOMATIONS.REFRESH', 'Recarregar')"
        @click="loadFields"
      >
        <span class="i-lucide-refresh-cw w-3 h-3" />
      </button>
    </div>

    <div
      v-if="loading"
      class="text-slate-500 italic py-2"
    >
      {{ $t('OMNI.SLOTS.API_AUTOMATIONS.LOADING', 'Carregando...') }}
    </div>
    <div
      v-else-if="error"
      class="text-red-600 dark:text-red-400 py-2"
    >
      {{ error }}
    </div>
    <div
      v-else-if="!fields.length"
      class="text-slate-500 italic py-2"
    >
      {{ $t('OMNI.SLOTS.API_AUTOMATIONS.NO_FIELDS', 'Nenhum field configurado nos slots.') }}
    </div>
    <div
      v-else
      class="max-h-72 overflow-y-auto space-y-3 pr-1"
    >
      <div
        v-for="(group, slot) in groupedFields"
        :key="slot"
      >
        <div class="text-[10px] uppercase tracking-wide text-slate-400 dark:text-slate-500 mb-1 px-1">
          {{ slot }}
        </div>
        <ul class="space-y-0.5">
          <li
            v-for="field in group"
            :key="field.name"
          >
            <button
              type="button"
              class="w-full flex items-center gap-2 px-2 py-1 rounded text-left hover:bg-woot-50 dark:hover:bg-slate-700 group"
              @click="pickField(field)"
            >
              <span
                class="w-3 h-3 shrink-0 text-slate-400"
                :class="sourceIcon(field.source_type)"
              />
              <span class="text-purple-600 dark:text-purple-400 font-mono font-semibold">{{ field.name }}</span>
              <span
                v-if="field.description"
                class="truncate text-slate-500 dark:text-slate-400 text-[11px]"
              >{{ field.description }}</span>
              <span class="ml-auto text-[10px] opacity-0 group-hover:opacity-100 text-woot-600 dark:text-woot-400">
                {{ $t('OMNI.SLOTS.API_AUTOMATIONS.INSERT', 'Inserir') }}
              </span>
            </button>
          </li>
        </ul>
      </div>
    </div>
  </div>
</template>
